import type { ChipClass } from "./shift-readiness.ts";

/**
 * `8 Proof` — DEL-07's proof of delivery, as the design draws it.
 *
 * The design's screen is one proof in one state: a signature illustration, the chip
 * `proof pending`, _"Delivered to Amina K."_, _"Signature and photo captured · checking…"_, three
 * thumbnails, the dashed note _"Shown as delivered only once proof is verified."_, and a
 * slide-to-confirm `Complete delivery`.
 *
 * Every one of those is derived here from `GET /v1/proofs/{proofId}` — except one, which has no
 * source at all and is therefore drawn as an absence rather than as a value:
 *
 *   > `Delivered to Amina K.` — who took the parcel — nothing publishes `proof.recipientName` or
 *   > `proof.evidence.recipientName`
 *
 * That is `tests/contract/mobile-screen-support.test.ts`'s own wording, and it is the single reason
 * that test marks this screen **degraded** rather than whole. DEL-07 publishes an evidence kind
 * `recipient_verification`, which says that the recipient *was* verified and never who they are, so
 * the name is not merely absent from one field — it is absent from the service. The title therefore
 * reads `Delivered to` followed by the design's own `unknown` mark, and the signature illustration
 * keeps the design's dashed box and draws no handwriting in it. Never a placeholder name, never a
 * dash, and never the courier's own name standing in for the recipient's.
 */

/** DEL-07's `verificationState`, exactly. */
export type VerificationState = "pending" | "verified" | "rejected";

/** DEL-07's `evidence[].kind`, exactly. */
export type EvidenceKind =
  "recipient_verification" | "signature" | "photo" | "barcode" | "location" | "courier_attestation";

export interface ProofEvidence {
  readonly evidenceId: string;
  readonly kind: EvidenceKind;
  readonly objectRef?: string;
  readonly sha256?: string;
  readonly consentPurpose?: string;
  readonly capturedAt: string;
}

/** The proof, as DEL-07 publishes it. Narrowed to the fields this screen reads. */
export interface ProofRecord {
  readonly proofId: string;
  readonly tenantId: string;
  readonly deliveryId: string;
  readonly deliveryVersion: number;
  readonly shipmentId: string;
  readonly stopId: string;
  readonly packageIds: readonly string[];
  readonly policyVersion: string;
  readonly evidence: readonly ProofEvidence[];
  readonly verificationState: VerificationState;
  readonly createdBy: string;
  readonly createdAt: string;
  readonly verifiedAt?: string;
  readonly rejectionReason?: string;
  readonly version: number;
}

/**
 * The thumbnail the design draws for each evidence kind: its glyph and its caption.
 *
 * The three the design draws are `['draw','Signature']`, `['photo_camera','Door']` and
 * `['inventory_2','Parcel']`, and they are kept word for word. The mapping from a published `kind`
 * to one of them is this build's reading of the design, and it is the reading that makes the
 * design's own sample come out as the design draws it: a signature, a photograph and the parcel's
 * barcode are what a courier captures at a door.
 *
 * Two things about it are worth being explicit, because both could be mistaken for carelessness:
 *
 *   - **`Door` is the design's caption for a `photo`, and nothing publishes what a photograph is
 *     of.** DEL-07's evidence carries a kind, an object reference, a digest and a consent purpose,
 *     and no caption. So a photograph of something other than a door is still captioned `Door`
 *     here. Recorded as Q-74 rather than silently corrected to the word `Photo`, because the rules
 *     say to keep the design's copy and this is the design's copy.
 *   - **The three kinds the design does not draw are drawn anyway.** A proof carrying a
 *     `recipient_verification`, a `location` or a `courier_attestation` would otherwise lose a
 *     thumbnail, and removing a captured piece of evidence from the screen that exists to show the
 *     evidence is worse than captioning it in this build's words. Those three are flagged.
 */
const THUMBNAIL: Readonly<
  Record<
    EvidenceKind,
    { readonly glyph: string; readonly label: string; readonly designed: boolean }
  >
> = {
  signature: { glyph: "draw", label: "Signature", designed: true },
  photo: { glyph: "photo_camera", label: "Door", designed: true },
  barcode: { glyph: "inventory_2", label: "Parcel", designed: true },
  recipient_verification: { glyph: "how_to_reg", label: "Recipient", designed: false },
  location: { glyph: "my_location", label: "Location", designed: false },
  courier_attestation: { glyph: "assignment_ind", label: "Attested", designed: false },
};

export interface ProofThumb {
  readonly glyph: string;
  readonly label: string;
}

/**
 * One thumbnail per captured piece of evidence, in the order DEL-07 published them.
 *
 * Not deduplicated by kind. Two photographs are two captures and the screen that exists to show
 * what was captured must show both; the design happens to draw three of three different kinds.
 */
export function proofThumbs(proof: ProofRecord): readonly ProofThumb[] {
  return proof.evidence.map((evidence) => ({
    glyph: THUMBNAIL[evidence.kind].glyph,
    label: THUMBNAIL[evidence.kind].label,
  }));
}

/** True when every thumbnail on this proof is one the design draws, so the flag can say when not. */
export function thumbsAreDesigned(proof: ProofRecord): boolean {
  return proof.evidence.every((evidence) => THUMBNAIL[evidence.kind].designed);
}

/**
 * The word the sub-line uses for each evidence kind, which is **not** the thumbnail's caption.
 *
 * The two say different things and the design uses two different vocabularies for them. A
 * thumbnail's caption says what that capture shows — `Door`, `Parcel` — and the sub-line names the
 * *kind* of evidence: the design writes `Signature and photo captured`, and `photo` is DEL-07's own
 * `kind`, not a caption. Composing the sentence from the captions produced
 * `Signature, door and parcel captured`, which reads as a list of objects rather than of evidence.
 *
 * Keeping them apart also makes the design's own sentence reproducible: a proof carrying a signature
 * and a photograph renders `Signature and photo captured · checking…`, word for word.
 */
const KIND_WORD: Readonly<Record<EvidenceKind, string>> = {
  signature: "signature",
  photo: "photo",
  barcode: "barcode",
  recipient_verification: "recipient check",
  location: "location",
  courier_attestation: "attestation",
};

/**
 * `Signature and photo captured · checking…` — the design's sub-line, composed from the evidence.
 *
 * The design's sample carries three pieces of evidence and its sentence names two of them, so on
 * that sample this is **not** a literal copy and the difference is deliberate. A fixed sentence
 * would tell a courier a signature was captured on a proof that carries only a photograph, which is
 * a claim about evidence in a dispute — the one place on this application where a confident wrong
 * word costs something.
 *
 * The measured cost is reported rather than closed: at 320dp the third kind makes the line wrap to
 * two, which moves the thumbnails and the note down by one 19px line. At 360 and 420 it fits on one
 * line and the difference is the words alone. Recorded as Q-75.
 *
 * `· checking…` is the design's, and is `verificationState: pending`. The other two states are not
 * drawn by the design and take this build's words.
 */
export function capturedLine(proof: ProofRecord): string {
  const kinds = [...new Set(proof.evidence.map((evidence) => KIND_WORD[evidence.kind]))];
  const named =
    kinds.length === 0 ? "Nothing captured" : `${sentenceCase(joinWithAnd(kinds))} captured`;

  switch (proof.verificationState) {
    case "pending":
      return `${named} · checking…`;
    case "verified":
      return `${named} · verified`;
    case "rejected":
      return proof.rejectionReason === undefined
        ? `${named} · not accepted`
        : `${named} · not accepted: ${proof.rejectionReason}`;
  }
}

/** `a`, `a and b`, `a, b and c` — the design's own connector on its own sample. */
function joinWithAnd(parts: readonly string[]): string {
  if (parts.length <= 1) return parts[0] ?? "";
  return `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]!}`;
}

function sentenceCase(value: string): string {
  return value.length === 0 ? value : value[0]!.toUpperCase() + value.slice(1);
}

/**
 * The state chip.
 *
 * `['unknown','proof pending']` is the design's, and `unknown` is right rather than merely available:
 * a proof DEL-07 has not verified is neither accepted nor refused, and this application's rule is
 * that a third value is never drawn as its nearest confident neighbour.
 */
export function proofChip(proof: ProofRecord): {
  readonly class: ChipClass;
  readonly label: string;
} {
  switch (proof.verificationState) {
    case "pending":
      return { class: "unknown", label: "proof pending" };
    case "verified":
      // `['done','verified']`, which `9 Earnings` is the design's own use of.
      return { class: "done", label: "verified" };
    case "rejected":
      return { class: "human", label: "action_required" };
  }
}

/** The dashed note. The design's sentence, kept, and one for each state it does not draw. */
export function proofNote(proof: ProofRecord): {
  readonly kind: "info" | "pending";
  readonly text: string;
} {
  switch (proof.verificationState) {
    case "pending":
      return { kind: "pending", text: "Shown as delivered only once proof is verified." };
    case "verified":
      return { kind: "info", text: "Proof verified. This delivery shows as delivered." };
    case "rejected":
      return {
        kind: "info",
        text:
          proof.rejectionReason === undefined
            ? "This proof wasn't accepted, so the delivery doesn't show as delivered yet."
            : `This proof wasn't accepted: ${proof.rejectionReason}`,
      };
  }
}

/** True when the words this screen draws are the design's, so the flag can say when they are not. */
export function proofWordingIsDesigned(proof: ProofRecord): boolean {
  return proof.verificationState === "pending" && thumbsAreDesigned(proof);
}

/**
 * Why `Complete delivery` cannot be carried out, in the published field names that are missing.
 *
 * The design's slide is the act of creating the proof — which is what
 * `tests/contract/mobile-screen-support.test.ts` names behind this screen's first element,
 * `POST /v1/proofs`. That route exists and is reachable. What does not exist is any way for a
 * courier's application to learn the six identifiers its body requires: they describe the job, and
 * every screen that would carry a job — `4 To pickup`, `5 Scan`, `Multi-stop job` — is blocked on a
 * read nothing publishes.
 *
 * So the button is drawn exactly as designed, because nothing may be removed, and says this when it
 * is pressed rather than appearing to work. Stated as the contract's own required field names, so
 * the sentence stops being true the moment they become reachable.
 */
export const COMPLETE_DELIVERY_UNAVAILABLE =
  "DEL-07 requires deliveryId, deliveryVersion, shipmentId, stopId, packageIds and policyVersion on a proof, and nothing published tells a courier's app the job it is on.";

/** What the title says about who took the parcel, which is that nothing published says. */
export const RECIPIENT_ABSENCE =
  "Nothing publishes who took the parcel: no proof.recipientName and no proof.evidence.recipientName.";
